import {Button, closeCurrentDialog, openDialog} from 'platform/components';
import {Box, Hide, Right, Show, Space, Stack, VStack} from 'platform/foundation';

import {useEffect} from 'react';

import {find, isNil, isNotNil, not} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty, isTruthy} from 'ramda-adjunct';

import {
  CustomerResponseBodyV2,
  useGetCustomerV2Query,
  useLazyGetVehicleCustomerQuery,
} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {useCreateServiceVehicleMutation, useLazyGetServiceVehicleQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {type ApiError, Nullish, suffixTestId, TestIdProps, useQueryState} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';
import {is404ApiError} from '../../utils/is404ApiError';
import {AftersalesVehicleWidgetCard} from '../AftersalesVehicleWidgetCard/AftersalesVehicleWidgetCard';
import {VehicleCreationRequest} from '../AftersalesVehicleWidgetCard/components/MatchingVehicles';
import {VehicleMatchOrCreate} from '../AftersalesVehicleWidgetCard/components/VehicleMatchOrCreate';
import {CustomerMatchOrCreate} from '../CustomerWidgetCard/components/CustomerMatchOrCreate';
import {CustomerWidgetCard} from '../CustomerWidgetCard/CustomerWidgetCard';
import {SuggestedVehicles} from './components/SuggestedVehicles';
import {useVehicleSmartSearch} from './hooks/useVehicleSmartSearch';

interface CustomerVehicleSelectionProps extends TestIdProps {
  customerId: string | Nullish;
  customerTooltip?: string;
  vehicleId: string | Nullish;
  allowNoVehicle?: boolean;
  onCustomerChange: (customerId: string | Nullish) => void;
  onVehicleChange: (vehicleId: string | Nullish) => void;
  onCreateVehicle?: (request: VehicleCreationRequest) => void;
  onEditVehicle?: (vehicleId: string) => void;
  onContinueClick?: () => void;
}

export function CustomerVehicleSelection(props: CustomerVehicleSelectionProps) {
  const [defaultCustomerId] = useQueryState('customerId');
  const [defaultVehicleId] = useQueryState('vehicleId');

  const [getVehicleCustomer, getVehicleCustomerQuery] = useLazyGetVehicleCustomerQuery();
  const [getServiceVehicle] = useLazyGetServiceVehicleQuery();
  const [createServiceVehicle] = useCreateServiceVehicleMutation();

  const {
    vehicles,
    searchQuery,
    isLoading,
    isError,
    onSearchChange,
    getSuggestedVehicles,
    resetSuggestedVehicles,
  } = useVehicleSmartSearch();

  const {currentData: customer} = useGetCustomerV2Query(
    {customerId: props.customerId ?? ''},
    {skip: !props.customerId}
  );
  const {data: vehicle, refetch: refetchVehicle} = useGetVehicleQuery(
    {vehicleId: props.vehicleId ?? ''},
    {
      skip: not(props.vehicleId),
      selectFromResult: (result) => (props.vehicleId ? result : {...result, data: null}),
    }
  );

  useEffect(
    function prefillCustomerAndVehicle() {
      if (isNotNilOrEmpty(defaultVehicleId) && isNilOrEmpty(defaultCustomerId)) {
        getVehicleCustomer({vehicleId: defaultVehicleId})
          .unwrap()
          .then((customers) => {
            const vehicleOperator = find(
              (ownership) => ownership?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR',
              customers ?? []
            );
            props.onCustomerChange(vehicleOperator?.customerId ?? null);
          })
          .catch(handleApiError);
      }

      if (isNotNilOrEmpty(defaultCustomerId) && isNilOrEmpty(defaultVehicleId)) {
        getSuggestedVehicles(defaultCustomerId ?? '');
      }

      if (isNotNilOrEmpty(defaultCustomerId) && isNotNilOrEmpty(defaultVehicleId)) {
        props.onCustomerChange(defaultCustomerId);
        props.onVehicleChange(defaultVehicleId);
      }
    },
    [defaultCustomerId, defaultVehicleId] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const handleVehicleSelect = async (vehicleId: string | Nullish) => {
    props.onVehicleChange(vehicleId ?? null);

    if (isNil(vehicleId)) {
      return;
    }

    await getServiceVehicle({vehicleId})
      .unwrap()
      .then(async (data) => {
        // METADA returns 200 with data: null, when service vehicle is not found from how :(
        if (isNil(data)) {
          await createServiceVehicle({vehicleId}).unwrap().catch(handleApiError);
        }
      })
      .catch(async (error: ApiError) => {
        if (is404ApiError(error)) {
          await createServiceVehicle({vehicleId}).unwrap().catch(handleApiError);
        } else {
          handleApiError(error);
        }
      });

    if (isNil(customer)) {
      if (props.vehicleId === vehicleId) {
        refetchVehicle();
      }

      await getVehicleCustomer({vehicleId})
        .unwrap()
        .then((customers) => {
          const vehicleOperator = find(
            (ownership) => ownership?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR',
            customers ?? []
          );
          props.onCustomerChange(vehicleOperator?.customerId ?? null);
        })
        .catch(handleApiError);
    }
  };

  const handleCustomerChange = () =>
    openDialog(
      <CustomerMatchOrCreate
        onCustomer={(customer) => {
          closeCurrentDialog();
          handleCustomerSelect(customer);
        }}
        data-testid={suffixTestId('customerChange', props)}
      />,
      {
        title: i18n.t('entity.customer.labels.customer'),
      }
    );

  const handleCustomerSelect = (selectedCustomer: CustomerResponseBodyV2) => {
    props.onCustomerChange(selectedCustomer.id);

    if (!vehicle) {
      getSuggestedVehicles(selectedCustomer.id);
    }
  };

  const handleSelectVehicle = () => {
    openDialog(
      <VehicleMatchOrCreate
        onVehicleSelect={(vehicleId) => {
          closeCurrentDialog();
          props.onVehicleChange(vehicleId);
          resetSuggestedVehicles();
        }}
        onCreateVehicle={
          // Conditional, or MatchingVehicles' truthiness check never reaches its fallback.
          isNotNil(props.onCreateVehicle)
            ? (request) => {
                // Mirrors onVehicleSelect above minus its closeCurrentDialog, so it keeps that
                // path's existing skip of the service-vehicle probe and operator auto-fill.
                closeCurrentDialog();
                props.onCreateVehicle?.({
                  prefill: request.prefill,
                  onCreated: (vehicleId) => {
                    props.onVehicleChange(vehicleId);
                    resetSuggestedVehicles();
                  },
                });
              }
            : undefined
        }
        data-testid={suffixTestId('finder', props)}
      />,
      {title: i18n.t('entity.vehicle.labels.vehicle')}
    );
  };

  return (
    <VStack spacing={2} height="100%">
      <Stack direction={['column', 'column', 'column', 'row']} spacing={4}>
        <Box flex={1}>
          <Show when={isTruthy(vehicles || searchQuery)}>
            <SuggestedVehicles
              selectedVehicle={vehicle ?? null}
              suggestedVehicles={vehicles ?? []}
              onVehicleChange={(vehicleId) => props.onVehicleChange(vehicleId ?? null)}
              onAddNew={handleSelectVehicle}
              onSearchChange={(query) => onSearchChange(props.customerId ?? '', query)}
              allowNoVehicle={props.allowNoVehicle}
              isLoading={
                getVehicleCustomerQuery.isLoading || getVehicleCustomerQuery.isFetching || isLoading
              }
              isError={getVehicleCustomerQuery.isError || isError}
              data-testid={suffixTestId('suggestedVehicles', props)}
            />
          </Show>
          <Hide when={vehicles || searchQuery}>
            <AftersalesVehicleWidgetCard
              onChange={handleVehicleSelect}
              onCreateVehicle={props.onCreateVehicle}
              onEditVehicle={props.onEditVehicle}
              vehicle={vehicle}
              data-testid={suffixTestId('vehicleWidgetCard', props)}
            />
          </Hide>
        </Box>
        <Box flex={1}>
          <CustomerWidgetCard
            tooltip={props.customerTooltip}
            onCustomer={handleCustomerSelect}
            onChange={handleCustomerChange}
            customerId={customer?.id}
            secondStepComponentType="SERVICE_CASE"
            hasVerification
            isDetailLinkVisible
            data-testid={suffixTestId('customerWidgetCard', props)}
          />
        </Box>
      </Stack>
      <Show when={props.onContinueClick}>
        <Space fillAvailable />
        <Right>
          <Button
            onClick={props.onContinueClick}
            variant="outlined"
            isDisabled={not(props.vehicleId) || not(props.customerId)}
            title={i18n.t('general.actions.continue')}
            data-testid={suffixTestId('continue', props)}
          />
        </Right>
      </Show>
    </VStack>
  );
}
